import { Suspense, useEffect } from "react";
import { HashRouter, Routes, Route, useLocation } from "react-router-dom";
import { AxiosInterceptor } from "./utils/index";
import routes from "./router";
import { useDispatch, useSelector } from "react-redux";
import { setDeviceGlobally } from "./app/commonplace";
import TopNav from "./components/top-nav";
import MobileBlocker from "./components/common/MobileBlocker";
import SidebarNav from "./components/sidebar-nav";
import RequireAuth from "./components/common/RequireAuth";
import RequireGrandmasterExperience from "./components/common/RequireGrandmasterExperience";
import { bootstrapRealtimeConnection } from "./realtime/realtimeClient";
import { SkillLevelNamingProvider } from "./contexts/SkillLevelNamingContext";
import { GrandmasterExperienceProvider } from "./contexts/GrandmasterExperienceContext";
import { CreateShiftProvider } from "./contexts/CreateShiftContext";

const AUTH_PAGE_PATHS = new Set([
  "/login",
  "/home",
  "/forget",
  "/resetPassword",
  "/accept-invite",
  "/delete",
]);

const AppRoutesLayout = ({
  userInfo,
  isSidebarMode,
  sidebarExpanded,
  isProfessionalMode,
  professionalSidebarExpanded,
}: {
  userInfo: any;
  isSidebarMode: boolean;
  sidebarExpanded: boolean;
  isProfessionalMode: boolean;
  professionalSidebarExpanded: boolean;
}) => {
  const location = useLocation();
  const isMessagesRoute = location.pathname === "/messages";
  const isDashboardRoute = location.pathname === "/dashboard" || location.pathname === "/overview";
  const isCalendarRoute = location.pathname === "/calendar";
  const isBankRoute = location.pathname === "/bank" || location.pathname.startsWith("/bank/");
  const isSocialRoute =
    location.pathname === "/social" || location.pathname.startsWith("/social/");
  const isAccountRoute = location.pathname.startsWith("/account");
  const pagePad = isMessagesRoute
    ? "flex min-h-0 flex-1 flex-col overflow-hidden"
    : isDashboardRoute
    ? "flex min-h-0 flex-1 flex-col overflow-hidden"
    : isAccountRoute
      ? "flex min-h-0 flex-1 flex-col overflow-hidden px-3 pt-4 md:px-6"
    : isCalendarRoute || isBankRoute || isSocialRoute
      ? "flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-3 pt-4 md:px-6"
    : "flex min-h-0 flex-1 flex-col px-3 pb-12 pt-5 md:px-6";
  const onAuthPage = AUTH_PAGE_PATHS.has(location.pathname);
  const loggedInLayout = isProfessionalMode
    ? "flex h-full min-h-0 flex-col"
    : pagePad;
  const useLoggedInPageChrome = Boolean(userInfo?.loginStatus) && !onAuthPage;

  return (
    <div
      className={`min-w-0 flex-1 ${isMessagesRoute || isDashboardRoute || isAccountRoute || isCalendarRoute || isBankRoute || isSocialRoute ? "overflow-hidden" : "overflow-y-auto"} ${useLoggedInPageChrome ? loggedInLayout : ""}`}
    >
      <div className={isDashboardRoute || isMessagesRoute ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden" : "flex min-h-0 min-w-0 flex-1 flex-col"}>
      <Routes>
        {routes.map((route, index) => {
          const isPublic = route.auth === false;
          let element = (
            <Suspense fallback={null}>
              <route.component routes={route.children ? route.children : []} />
            </Suspense>
          );
          if (route.grandmasterOnly) {
            element = (
              <RequireGrandmasterExperience>{element}</RequireGrandmasterExperience>
            );
          }
          return (
            <Route
              path={route.path}
              key={index}
              element={isPublic ? element : <RequireAuth>{element}</RequireAuth>}
            />
          );
        })}
      </Routes>
      </div>
    </div>
  );
};

const AppShell = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const userInfo = useSelector((state: any) => state.userInfo);
  const onAuthPage = AUTH_PAGE_PATHS.has(location.pathname);
  const showAppChrome = Boolean(userInfo?.loginStatus) && !onAuthPage;
  // according to device types (computer / ipad / phone) to use different css
  useEffect(() => {
    let u = navigator.userAgent;
    let deviceType = "" as string;
    if (!!u.match(/compatible/i) || u.match(/Windows/i)) {
      deviceType = "computer";
    } else if (u.match(/Macintosh/i) || u.match(/MacIntel/i)) {
      deviceType = "computer";
    } else if (u.match(/iphone/i) || u.match(/Ipad/i)) {
      deviceType = "h5";
    } else if (u.match(/android/i)) {
      deviceType = "h5";
    } else {
      deviceType = "computer";
    }
    dispatch(
      setDeviceGlobally({
        device: deviceType,
      }),
    );
  }, []);

  useEffect(() => {
    if (!userInfo?.loginStatus) return;
    void bootstrapRealtimeConnection().catch(() => {
      // errors logged in realtimeClient
    });
  }, [userInfo?.loginStatus]);

  return (
    <>
      <MobileBlocker />
      <div
        className={
          showAppChrome
            ? "flex h-screen flex-col overflow-hidden bg-background text-foreground"
            : "min-h-screen bg-white"
        }
      >
        {showAppChrome && <TopNav hideRouteTabs />}
        <div className={showAppChrome ? "flex min-h-0 flex-1 overflow-hidden pt-14" : undefined}>
          {showAppChrome && <SidebarNav />}
          <AppRoutesLayout
            userInfo={userInfo}
            isSidebarMode={showAppChrome}
            sidebarExpanded
            isProfessionalMode={false}
            professionalSidebarExpanded={false}
          />
        </div>
      </div>
    </>
  );
};

const App = () => {
  return (
    <HashRouter
      future={{
        v7_startTransition: false,
        v7_relativeSplatPath: true,
      }}
    >
      <AxiosInterceptor>
        <SkillLevelNamingProvider>
        <GrandmasterExperienceProvider>
        <CreateShiftProvider>
          <AppShell />
        </CreateShiftProvider>
        </GrandmasterExperienceProvider>
        </SkillLevelNamingProvider>
      </AxiosInterceptor>
    </HashRouter>
  );
};

export default App;
